/**
 * What actually happened, per unit — shown after the write completes.
 *
 * The receipt reports outcomes from the server rather than the staged plan, so
 * a unit that was already where it was headed reads "Unchanged" instead of
 * claiming a move that never occurred.
 */

import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { formatDayRange } from '@/lib/unitCodeParser';
import type { AssignmentResult } from './types';
import { didChange } from './AssignReceipt.helpers';

interface AssignReceiptProps {
  results: AssignmentResult[];
  warnings: string[];
  /** Colour per destination, matching the drop targets the user dropped onto. */
  colorFor: (result: AssignmentResult) => string;
}

function describe(result: AssignmentResult): string {
  const parts: string[] = [];

  if (result.outcome === 'failed') parts.push('Not written — try again');
  else if (result.outcome === 'unchanged')
    parts.push(didChange(result) ? 'Already here' : 'Unchanged');
  else if (result.outcome === 'moved')
    parts.push(`Moved from ${result.fromGroupName ?? 'unassigned'}`);
  else parts.push('Assigned');

  if (result.isSite && result.outcome !== 'failed') parts[0] += ` → ${result.toGroupName}`;

  const range = formatDayRange(result.startDate ?? '', result.endDate ?? '');
  if (range) parts.push(range);
  if (result.activatedFrom) parts.push(`${result.activatedFrom} → Active`);

  return parts.join(' · ');
}

export function AssignReceipt({ results, warnings, colorFor }: AssignReceiptProps) {
  const failed = results.filter((r) => r.outcome === 'failed').length;
  // 'unchanged' describes PLACEMENT only — a unit already on the destination
  // can still have been activated or dated. Counting those as untouched
  // under-reported the work and suppressed the success toast entirely.
  const updated = results.filter((r) => r.outcome !== 'failed' && didChange(r)).length;
  const unchanged = results.length - failed - updated;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        {failed > 0 ? (
          <AlertTriangle className="h-[18px] w-[18px] text-yellow-400" />
        ) : (
          <CheckCircle2 className="h-[18px] w-[18px] text-[#8FB560]" />
        )}
        <span className="text-[15px] font-semibold text-foreground">
          {[
            `${updated} updated`,
            `${unchanged} unchanged`,
            failed ? `${failed} failed` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>

      {warnings.length > 0 && (
        <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2">
          {warnings.map((warning) => (
            <p key={warning} className="text-[11px] text-yellow-400">
              {warning}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-[3px]">
        {results.map((result) => (
          <div
            key={result.unitId}
            className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-[7px]"
            style={{
              borderLeft: `3px solid ${
                result.outcome === 'failed'
                  ? 'rgb(250 204 21)'
                  : result.outcome === 'unchanged'
                    ? 'hsl(var(--border))'
                    : colorFor(result)
              }`,
            }}
          >
            <span className="flex-none font-mono text-[13px] text-foreground">
              {result.unitCode}
            </span>
            <span className="min-w-0 flex-1 text-[11px] text-muted-foreground">
              {describe(result)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
